/* eslint-disable no-extra-boolean-cast */
import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import useGetAccounts from '@hooks/Finance/ChartAccountsDetails/useGetAccounts';
import useGetBanks from '@hooks/Lookups/use-get-Banks';
import { AddBankAccountsSchema } from '../../../ValidationForm/validationSchema';
import {
  getApBankAccountsList,
  getApBankAccountsById,
  updateApBankAccounts
} from '@store/Finance/AP/ApBankAccounts/ApBankAccountsSlice';
import Loading from '@components/Shared/Loader/Loading';
import useGetOperatingUnitWithPermission from '@hooks/NTX/Organization/useGetOperatingUnitWithPermission';

interface props {
  ID?: string;
}

const UpdateBankAccounts: React.FC<props> = ({ ID }) => {
  const { i18n, t } = useTranslation();
  const dispatch = useAppDispatch();
  const { OptionsAccounts, loading, error } = useGetAccounts();
  const { OperatingUnitOptions, loading: loading1, error: error1 } = useGetOperatingUnitWithPermission();

  const { Banks, loading: loading2, error: error2 } = useGetBanks();

  useEffect(() => {
    if (ID) {
      dispatch(getApBankAccountsById(ID));
    }
  }, [ID, dispatch]);
  const { ApBankAccountsById: data, loading: loading3, error: error3 } = useAppSelector(a => a.ApBankAccounts);

  // console.log(data);

  const formik = useFormik({
    initialValues: {
      ntxBankId: data.ntxBankId ?? '',
      name: data.name ?? '',
      name2: data.name2 ?? '',
      description: data.description ?? '',
      bankAccount: data.bankAccount ?? '',
      accountId: data.accountId ?? '',
      accountReconcileId: data.accountReconcileId ?? '',
      iban: data.iban ?? '',
      organizationId: data.organizationId ?? ''
    },
    enableReinitialize: true,
    validationSchema: AddBankAccountsSchema(t),
    onSubmit: values => {
      dispatch(
        updateApBankAccounts({
          id: data.id,
          name: values.name,
          name2: values.name2,
          ntxBankId: values.ntxBankId,
          bankAccount: values.bankAccount,
          iban: values.iban,
          description: values.description,

          organizationId: values.organizationId,
          accountId: values.accountId,
          accountReconcileId: values.accountReconcileId
        })
      )
        .unwrap()
        .then((res: any) => {
          if (res?.succeeded === true) {
            CustomAlert({ action: 'Add' });
            document.getElementById('closeModalForm')?.click();
            dispatch(getApBankAccountsList());
            formik.resetForm();
          } else {
            formik.setSubmitting(false);
            CustomAlert({ action: 'Error', msg: res?.message });
          }
        })
        .catch((error: any) => {
          formik.setSubmitting(false);
          CustomAlert({ action: 'Error' });
        });
    }
  });

  return (
    <Loading loading={loading || loading2 || loading3} error={error || error2 || error3}>
      <Form onSubmit={formik.handleSubmit}>
        <div className='row'>
          <div className='col-xl-6'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('LocalName')}</label>
              <Form.Control
                className='form-control  '
                type='text'
                autoComplete='off'
                name='name2'
                onChange={formik.handleChange}
                value={formik.values.name2}
                isInvalid={!!(formik.touched.name2 && formik.errors.name2)}
              />
              {/* <Form.Control.Feedback type="invalid">{formik.errors.name2}</Form.Control.Feedback> */}
            </div>
          </div>
          <div className='col-xl-6'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('GlobalName')}</label>
              <Form.Control
                className='form-control  '
                type='text'
                autoComplete='off'
                name='name'
                onChange={formik.handleChange}
                value={formik.values.name}
                isInvalid={!!(formik.touched.name && formik.errors.name)}
              />
              {/* <Form.Control.Feedback type="invalid">{formik.errors.name}</Form.Control.Feedback> */}
            </div>
          </div>

          {/* ✅ Operating Unit */}
          <div className='col-xl-6'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('OperatingUnit') || 'Operating Unit'}</label>
              <Select
                classNamePrefix='react-select'
                className={`w-100 ${!!(formik.touched.organizationId && formik.errors.organizationId) ? 'border border-danger' : ''}`}
                isLoading={loading1}
                isSearchable
                isClearable
                name='organizationId'
                options={OperatingUnitOptions}
                placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                onChange={(option: any) => {
                  formik.setFieldValue('organizationId', option ? option.value : null);
                }}
                value={OperatingUnitOptions?.find((f: any) => f.value === formik.values.organizationId) ?? null}
              />
              <div className='text-danger small'>
                {formik.touched.organizationId && (formik.errors.organizationId as any)}
              </div>
            </div>
          </div>

          <div className='col-xl-6 '>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('Bank')}</label>
              <Form.Select
                className='form-select'
                name='ntxBankId'
                onChange={formik.handleChange}
                value={formik.values.ntxBankId || ''}
                isInvalid={!!(formik.touched.ntxBankId && formik.errors.ntxBankId)}
              >
                <option value=''>{i18n.language === 'ar' ? 'إختر' : 'Choose'}</option>
                {Banks &&
                  Banks?.map((item: any, idx: any) => (
                    <option key={++idx} value={item?.id}>
                      {i18n.language === 'ar' ? item?.name2 : item?.name}
                    </option>
                  ))}
              </Form.Select>
              {/* <Form.Control.Feedback type="invalid">
              {formik.errors.ntxBankId}
            </Form.Control.Feedback> */}
            </div>
          </div>

          <div className='col-xl-6'>
            <label className='form-label'>{t('NoBank')}</label>
            <div className='input-group  '>
              <Form.Control
                className='form-control'
                type='text'
                autoComplete='off'
                name='bankAccount'
                onChange={formik.handleChange}
                value={formik.values.bankAccount}
                isInvalid={!!(formik.touched.bankAccount && formik.errors.bankAccount)}
              />
            </div>
          </div>

          <div className='col-xl-6 mb-3'>
            <label className='form-label'>{t('Iban')}</label>
            <div className='input-group flex-nowrap'>
              <Form.Control
                className='form-control'
                type='text'
                autoComplete='off'
                name='iban'
                onChange={formik.handleChange}
                value={formik.values.iban}
                isInvalid={!!(formik.touched.iban && formik.errors.iban)}
              />
              <span className='input-group-text'>SA</span>
              {/* <Form.Control.Feedback type="invalid">{formik.errors.iban}</Form.Control.Feedback> */}
            </div>
          </div>

          <div className='col-xl-6 '>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('Accounts')}</label>
              <Select
                classNamePrefix='react-select'
                className={`w-100 ${
                  formik.touched.accountId && formik.errors.accountId ? 'border border-danger' : ''
                } `}
                isLoading={loading}
                isSearchable={true}
                isClearable
                name='accountId'
                options={OptionsAccounts}
                placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                onChange={(option: any) => {
                  formik.setFieldValue('accountId', option === null ? null : option.value);
                }}
                value={OptionsAccounts?.find(f => f.value === formik.values.accountId)}
              />
            </div>
          </div>
          <div className='col-xl-6 '>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('حساب التسوية')}</label>
              <Select
                classNamePrefix='react-select'
                className={`w-100 ${
                  formik.touched.accountReconcileId && formik.errors.accountReconcileId ? 'border border-danger' : ''
                } `}
                isLoading={loading}
                isSearchable={true}
                isClearable
                name='accountReconcileId'
                options={OptionsAccounts}
                placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                onChange={(option: any) => {
                  formik.setFieldValue('accountReconcileId', option === null ? null : option.value);
                }}
                value={OptionsAccounts?.find(f => f.value === formik.values.accountReconcileId)}
              />
            </div>
          </div>

          {/* <div className='col-xl-12'>
            <div className='form-group mb-3'>
              <label className='form-label'>{t('Note')}</label>
              <Form.Control
                className='form-control  '
                type='text'
                autoComplete='off'
                name='description'
                onChange={formik.handleChange}
                value={formik.values.description}
                isInvalid={!!(formik.touched.description && formik.errors.description)}
              />
            </div>
          </div> */}
        </div>

        {/* <div className="text-center">
          <button disabled={formik.isSubmitting} type="submit" className="btn me-1 btn-theme mb-1">
            {formik.isSubmitting ? (
              <div className="spinner-border spinner-border-sm me-2"></div>
            ) : (
              <i className="fa fa-save fa-fw me-1"></i>
            )}
            {t('Save')}
          </button>
          <button
            type="button"
            className=" btn me-1 btn-default mb-1"
            data-bs-dismiss="modal"
            id="closeModalForm">
            <i className="fa fa-power-off fa-fw me-1"></i> {t('Cancel')}
          </button>
        </div> */}

        <div className='text-center mt-2'>
          <div className='d-flex justify-content-between gap-2'>
            <button disabled={formik.isSubmitting} type='submit' className='btn btn-lg btn-theme mb-1 w-100 rounded-0'>
              {formik.isSubmitting ? (
                <div className='spinner-border spinner-border-sm me-2'></div>
              ) : (
                <i className='fas fa-lg fa-fw me-2 fa fa-save'></i>
              )}
              {t('Save')}
            </button>
            <button
              type='button'
              className='btn btn-lg  btn-default mb-1 w-100 rounded-0'
              data-bs-dismiss='modal'
              id='closeModalForm'
            >
              <i className='fa fa-power-off fa-fw me-1'></i> {t('Cancel')}
            </button>
          </div>
        </div>
      </Form>
    </Loading>
  );
};

export default UpdateBankAccounts;
